<template>
  <Disclosure as="nav" class="bg-white shadow-lg fixed top-0 left-0 right-0 z-40" v-slot="{ open }">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-24">
        <div class="flex items-center">
          <router-link to="/" class="flex-shrink-0 flex items-center">
            <img 
              class="h-26 w-auto" 
              :src="charte.logo || '/logo.svg'" 
              alt="Breizhteam Connections" 
              @error="handleLogoError"
              style="height: 6.5rem;"
            />
            <span class="ml-4 text-2xl font-bold leading-tight" :style="{ color: charte.colors.primary }">
              BreizhTeam<br>
              <span class="text-lg font-medium">Connections</span>
            </span>
          </router-link>
          
          <!-- Desktop Menu -->
          <div class="hidden md:ml-8 md:flex md:space-x-8">
            <router-link 
              v-for="item in navigationItems" 
              :key="item.name"
              :to="item.path"
              class="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200"
              :class="{
                'border-primary-500 text-gray-900': $route.path === item.path
              }"
            >
              <component :is="item.icon" class="h-4 w-4 mr-1" />
              {{ item.name }}
            </router-link>
          </div>
        </div>
        
        <!-- User Menu -->
        <div class="hidden md:ml-6 md:flex md:items-center">
          <div v-if="isAuthenticated" class="ml-3 relative">
            <Menu as="div" class="relative">
              <MenuButton class="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                <span class="sr-only">Open user menu</span>
                <div class="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                  <span class="text-white text-sm font-medium">
                    {{ user?.name?.charAt(0).toUpperCase() }}
                  </span>
                </div>
              </MenuButton>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div class="py-1">
                    <MenuItem v-slot="{ active }">
                      <router-link
                        to="/profile"
                        :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                      >
                        Profil
                      </router-link>
                    </MenuItem>
                    <MenuItem v-if="isAdmin" v-slot="{ active }">
                      <router-link
                        to="/admin"
                        :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                      >
                        Administration
                      </router-link>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                      <button
                        @click="handleLogout"
                        :class="[active ? 'bg-gray-100' : '', 'block w-full text-left px-4 py-2 text-sm text-gray-700']"
                      >
                        Se déconnecter
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>
          <div v-else class="space-x-4">
            <router-link to="/login" class="text-gray-500 hover:text-gray-900">
              Se connecter
            </router-link>
            <router-link to="/register" class="btn-primary">
              S'inscrire
            </router-link>
          </div>
        </div>
        
        <!-- Mobile menu button -->
        <div class="md:hidden flex items-center">
          <DisclosureButton class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500">
            <span class="sr-only">Open main menu</span>
            <Bars3Icon v-if="!open" class="block h-6 w-6" aria-hidden="true" />
            <XMarkIcon v-else class="block h-6 w-6" aria-hidden="true" />
          </DisclosureButton>
        </div>
      </div>
    </div>
    
    <!-- Mobile menu -->
    <DisclosurePanel class="md:hidden">
      <div class="pt-2 pb-3 space-y-1">
        <DisclosureButton
          v-for="item in navigationItems"
          :key="item.name"
          as="router-link"
          :to="item.path"
          class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
          :class="{
            'bg-primary-50 border-primary-500 text-primary-700': $route.path === item.path
          }"
        >
          {{ item.name }}
        </DisclosureButton>
      </div>
      
      <div v-if="isAuthenticated" class="pt-4 pb-3 border-t border-gray-200">
        <div class="flex items-center px-4">
          <div class="flex-shrink-0">
            <div class="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
              <span class="text-white font-medium">
                {{ user?.name?.charAt(0).toUpperCase() }}
              </span>
            </div>
          </div>
          <div class="ml-3">
            <div class="text-base font-medium text-gray-800">{{ user?.name }}</div>
            <div class="text-sm font-medium text-gray-500">{{ user?.email }}</div>
          </div>
        </div>
        <div class="mt-3 space-y-1">
          <DisclosureButton
            as="router-link"
            to="/profile"
            class="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
          >
            Profil
          </DisclosureButton>
          <DisclosureButton
            v-if="isAdmin"
            as="router-link"
            to="/admin"
            class="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
          >
            Administration
          </DisclosureButton>
          <DisclosureButton
            as="button"
            @click="handleLogout"
            class="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
          >
            Se déconnecter
          </DisclosureButton>
        </div>
      </div>
      
      <div v-else class="pt-4 pb-3 border-t border-gray-200">
        <div class="space-y-1">
          <DisclosureButton
            as="router-link"
            to="/login"
            class="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
          >
            Se connecter
          </DisclosureButton>
          <DisclosureButton
            as="router-link"
            to="/register"
            class="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
          >
            S'inscrire
          </DisclosureButton>
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { Bars3Icon, XMarkIcon, HomeIcon, UsersIcon, CalendarDaysIcon, ChatBubbleLeftRightIcon, MapPinIcon, Squares2X2Icon, CubeIcon } from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import { useCharteGraphique } from '@/composables/useCharteGraphique'

const router = useRouter()
const authStore = useAuthStore()
const { charte } = useCharteGraphique()

const isAuthenticated = computed(() => authStore.isAuthenticated)
const isAdmin = computed(() => authStore.isAdmin)
const user = computed(() => authStore.user)

const navigationItems = computed(() => {
  const items = [
    { name: 'Accueil', path: '/', icon: HomeIcon }
  ]
  
  // Pages publiques avec style Breizhteam
  items.push(
    { name: '⎈ Les Navigateurs d\'Affaires', path: '/navigateurs-affaires', icon: UsersIcon },
    { name: '♖ Les Phares du Networking', path: '/phares-networking', icon: MapPinIcon },
    { name: '♥ Prochains RDV', path: '/events', icon: CalendarDaysIcon }
  )
  
  if (isAuthenticated.value) {
    // Pages privées membres
    items.push(
      { name: 'Applications', path: '/apps', icon: CubeIcon }
    )
  }
  
  return items
})

const handleLogout = () => {
  authStore.logout()
  router.push('/')
}

const handleLogoError = (event: Event) => {
  // Fallback to default logo if charte logo fails
  const img = event.target as HTMLImageElement
  img.src = '/logo.svg'
}
</script>